import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RecaptchaVerifier, signInWithPhoneNumber
} from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { ref, get, push, set } from 'firebase/database';
import { useLanguage } from '../LanguageContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [screen, setScreen] = useState('welcome');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        if (parsed.name && parsed.name !== 'Guest User') {
          navigate('/home');
        }
      } catch (e) {}
    }
  }, [navigate]);

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const confirmObjRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [name, setName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [gender, setGender] = useState('');
  const [signupOtpStep, setSignupOtpStep] = useState(false);
  const [signupOtp, setSignupOtp] = useState('');
  const signupConfirmObjRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!recaptchaRef.current && document.getElementById('recaptcha-container')) {
        recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const buildCurrentUser = (u, userId) => ({
    userId,
    name: u.name,
    phone: u.phone,
    email: u.email,
    city: u.city,
    gender: u.gender,
    vehicle: u.vehicle || '',
    initials: u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
  });

  const skipLogin = () => {
    const guestUser = {
      name: 'Guest User',
      phone: 'Not set',
      email: 'Not set',
      city: 'India',
      initials: 'GU',
    };
    localStorage.setItem('guningo_user', JSON.stringify(guestUser));
    navigate('/home');
  };

  const sendOTP = async () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phone.trim())) {
      alert('Indian mobile numbers start with 6-9 and have 10 digits');
      return;
    }
    setLoading(true);
    try {
      const usersRef = ref(db, 'users/');
      const snapshot = await get(usersRef);
      const data = snapshot.val();
      const fullPhone = '+91' + phone.trim();
      let userFound = null;
      let userKey = null;
      if (data) {
        Object.keys(data).forEach(key => {
          if (data[key].phone === fullPhone) {
            userFound = data[key];
            userKey = key;
          }
        });
      }
      if (!userFound) {
        setLoading(false);
        alert('No account exists with this number. Please create a new account first.');
        setScreen('signup');
        return;
      }
      setFoundUser({ ...userFound, userId: userKey });
      const confirmation = await signInWithPhoneNumber(auth, fullPhone, recaptchaRef.current);
      confirmObjRef.current = confirmation;
      setLoading(false);
      setScreen('otp');
    } catch (error) {
      setLoading(false);
      console.error('FULL ERROR OBJECT:', error);
      alert('Could not send OTP: ' + error.code + ' | ' + error.message);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      alert('Please enter the 6-digit OTP!');
      return;
    }
    setLoading(true);
    try {
      await confirmObjRef.current.confirm(otp);
      const userData = buildCurrentUser(foundUser, foundUser.userId);
      localStorage.setItem('guningo_user', JSON.stringify(userData));
      setLoading(false);
      navigate('/home');
    } catch (error) {
      setLoading(false);
      alert('Wrong OTP. Please check and try again.');
    }
  };

  const sendSignupOTP = async () => {
    if (!name.trim() || name.trim().length < 3) {
      alert('Please enter your full name');
      return;
    }
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(signupPhone.trim())) {
      alert('Indian mobile numbers start with 6-9 and have 10 digits');
      return;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      alert('Please enter a valid email');
      return;
    }
    if (!city.trim()) {
      alert('Please enter your city');
      return;
    }
    setLoading(true);
    try {
      const usersRef = ref(db, 'users/');
      const snapshot = await get(usersRef);
      const data = snapshot.val();
      const fullPhone = '+91' + signupPhone.trim();
      if (data) {
        const phoneExists = Object.values(data).some(u => u.phone === fullPhone);
        if (phoneExists) {
          setLoading(false);
          alert('This number already has an account. Please login instead.');
          setScreen('login');
          return;
        }
      }
      const confirmation = await signInWithPhoneNumber(auth, fullPhone, recaptchaRef.current);
      signupConfirmObjRef.current = confirmation;
      setLoading(false);
      setSignupOtpStep(true);
    } catch (error) {
      setLoading(false);
      console.error('FULL ERROR OBJECT:', error);
      alert('Could not send OTP: ' + error.code + ' | ' + error.message);
    }
  };

  const verifySignupOTP = async () => {
    if (!signupOtp || signupOtp.length !== 6) {
      alert('Please enter the 6-digit OTP!');
      return;
    }
    setLoading(true);
    try {
      await signupConfirmObjRef.current.confirm(signupOtp);
      const usersRef = ref(db, 'users/');
      const newUserRef = push(usersRef);
      const newUserData = {
        name: name.trim(),
        phone: '+91' + signupPhone.trim(),
        email: email.trim().toLowerCase(),
        city: city.trim(),
        gender: gender || 'Not specified',
        vehicle: '',
        rating: 0,
        trips: 0,
        docsVerified: false,
        createdAt: new Date().toISOString(),
      };
      await set(newUserRef, newUserData);
      const userData = buildCurrentUser(newUserData, newUserRef.key);
      localStorage.setItem('guningo_user', JSON.stringify(userData));
      setLoading(false);
      navigate('/home');
    } catch (error) {
      setLoading(false);
      alert('Wrong OTP. Please check and try again.');
    }
  };

  return (
    <div style={styles.page}>
      <div id="recaptcha-container"></div>

      <div style={styles.navbar}>
        <div style={styles.navLogo}>🚗 Guningo</div>
        <div style={styles.langToggle}>
          <button style={language === 'en' ? styles.langBtnActive : styles.langBtn} onClick={() => setLanguage('en')}>EN</button>
          <button style={language === 'hi' ? styles.langBtnActive : styles.langBtn} onClick={() => setLanguage('hi')}>हि</button>
        </div>
      </div>

      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          {screen === 'welcome' && (
            <>
              <h1 style={styles.headline}>{t('shareRides')}</h1>
              <p style={styles.subheadline}>{t('tagline')}</p>
              <div style={styles.card}>
                <button style={styles.primaryBtn} onClick={() => setScreen('signup')}>{t('createAccount')}</button>
                <button style={styles.secondaryBtn} onClick={() => setScreen('login')}>{t('alreadyHaveAccount')}</button>
                <p style={styles.switchLink} onClick={skipLogin}>{t('skip')} →</p>
              </div>
            </>
          )}

          {screen === 'login' && (
            <div style={styles.card}>
              <button style={styles.backLink} onClick={() => setScreen('welcome')}>← Back</button>
              <h2 style={styles.formTitle}>{t('welcomeBack')}</h2>
              <div style={styles.phoneRow}>
                <span style={styles.countryCode}>🇮🇳 +91</span>
                <input style={styles.input} placeholder="Mobile number" value={phone}
                  onChange={e => setPhone(e.target.value)} maxLength={10} />
              </div>
              <button style={styles.primaryBtn} onClick={sendOTP} disabled={loading}>
                {loading ? 'Sending...' : t('sendOtp') + ' →'}
              </button>
              <p style={styles.switchLink} onClick={() => setScreen('signup')}>New to Guningo? <b>Create account</b></p>
            </div>
          )}

          {screen === 'otp' && (
            <div style={styles.card}>
              <button style={styles.backLink} onClick={() => setScreen('login')}>← Change number</button>
              <h2 style={styles.formTitle}>{t('enterOtp')}</h2>
              <p style={styles.formSub}>Sent via SMS to +91 {phone}</p>
              <input style={styles.otpInput} placeholder="••••••" value={otp}
                onChange={e => setOtp(e.target.value)} maxLength={6} />
              <button style={styles.primaryBtn} onClick={verifyOTP} disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Login ✅'}
              </button>
            </div>
          )}

          {screen === 'signup' && !signupOtpStep && (
            <div style={styles.card}>
              <button style={styles.backLink} onClick={() => setScreen('welcome')}>← Back</button>
              <h2 style={styles.formTitle}>Create your account</h2>
              <input style={styles.input} placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} />
              <div style={styles.phoneRow}>
                <span style={styles.countryCode}>🇮🇳 +91</span>
                <input style={styles.input} placeholder="Mobile number" value={signupPhone}
                  onChange={e => setSignupPhone(e.target.value)} maxLength={10} />
              </div>
              <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
              <input style={styles.input} placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
              <button style={styles.primaryBtn} onClick={sendSignupOTP} disabled={loading}>
                {loading ? 'Sending...' : t('sendOtp') + ' →'}
              </button>
              <p style={styles.switchLink} onClick={() => setScreen('login')}>Already have an account? <b>Login</b></p>
            </div>
          )}

          {screen === 'signup' && signupOtpStep && (
            <div style={styles.card}>
              <button style={styles.backLink} onClick={() => setSignupOtpStep(false)}>← Edit details</button>
              <h2 style={styles.formTitle}>{t('enterOtp')}</h2>
              <p style={styles.formSub}>Sent via SMS to +91 {signupPhone}</p>
              <input style={styles.otpInput} placeholder="••••••" value={signupOtp}
                onChange={e => setSignupOtp(e.target.value)} maxLength={6} />
              <button style={styles.primaryBtn} onClick={verifySignupOTP} disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Create Account 🎉'}
              </button>
            </div>
          )}
        </div>

        <div style={styles.heroRight}>
          <svg width="100%" viewBox="0 0 260 160" style={{ maxWidth: 320 }}>
            <ellipse cx="130" cy="145" rx="100" ry="8" fill="#c7dff0" />
            <path d="M40 100 Q35 75 65 68 L90 45 Q110 32 140 32 L175 36 Q205 42 218 62 L235 68 Q248 74 245 92 L240 98 L40 98 Z" fill="#3a8fd0" />
            <path d="M95 48 Q115 36 140 36 L170 40 Q192 45 205 60 L100 60 Z" fill="#eaf5fc" />
            <circle cx="90" cy="105" r="7" fill="#eaf5fc" />
            <circle cx="130" cy="105" r="7" fill="#eaf5fc" />
            <circle cx="170" cy="105" r="7" fill="#eaf5fc" />
            <circle cx="75" cy="102" r="22" fill="#1a1a2e" />
            <circle cx="75" cy="102" r="9" fill="#eaf5fc" />
            <circle cx="205" cy="102" r="22" fill="#1a1a2e" />
            <circle cx="205" cy="102" r="9" fill="#eaf5fc" />
          </svg>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  navbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 30px', borderBottom: '1px solid #eef1f8',
  },
  navLogo: { fontSize: 18, fontWeight: 800, color: '#1a1a2e' },
  langToggle: { display: 'flex', gap: 4 },
  langBtn: { border: '1px solid #eef1f8', background: '#f7f9fc', borderRadius: 6, padding: '5px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#5a5f7d' },
  langBtnActive: { border: '1px solid #3a8fd0', background: '#3a8fd0', borderRadius: 6, padding: '5px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#fff' },
  hero: {
    background: '#eaf5fc', padding: '50px 40px', display: 'flex', alignItems: 'center',
    gap: 40, flexWrap: 'wrap', minHeight: 'calc(100vh - 65px)',
  },
  heroLeft: { flex: 1, minWidth: 300, maxWidth: 460 },
  heroRight: { flex: 1, minWidth: 260, display: 'flex', justifyContent: 'center' },
  headline: { fontSize: 32, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 8px' },
  subheadline: { fontSize: 15, fontWeight: 600, color: '#3a8fd0', margin: '0 0 24px' },
  card: {
    background: '#ffffff', border: '1.5px solid #d9edfa', borderRadius: 16,
    padding: 26, boxShadow: '0 8px 24px rgba(58,143,208,0.1)',
  },
  primaryBtn: {
    width: '100%', height: 50, background: '#3a8fd0', color: '#fff', border: 'none',
    borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10,
  },
  secondaryBtn: {
    width: '100%', height: 50, background: '#ffffff', color: '#1a1a2e',
    border: '1.5px solid #d9edfa', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  backLink: {
    background: 'none', border: 'none', color: '#3a8fd0', fontWeight: 600,
    cursor: 'pointer', padding: 0, marginBottom: 14, fontSize: 14,
  },
  formTitle: { color: '#1a1a2e', marginBottom: 4, marginTop: 0 },
  formSub: { color: '#5a5f7d', fontSize: 13, marginBottom: 16 },
  phoneRow: { display: 'flex', gap: 8, marginBottom: 14 },
  countryCode: {
    background: '#eaf5fc', borderRadius: 8, padding: '12px 14px',
    fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center',
  },
  input: {
    width: '100%', height: 46, borderRadius: 8, border: '1.5px solid #e2e5f0',
    padding: '0 14px', fontSize: 15, marginBottom: 14, boxSizing: 'border-box',
  },
  otpInput: {
    width: '100%', height: 56, borderRadius: 10, border: '1.5px solid #e2e5f0',
    fontSize: 22, textAlign: 'center', letterSpacing: 8, marginBottom: 16, boxSizing: 'border-box',
  },
  switchLink: { textAlign: 'center', color: '#5a5f7d', fontSize: 14, cursor: 'pointer', marginTop: 12 },
};